// Sticky bars that pin directly UNDER the page header (farm audit X-14, W-18).
//
// `<PageHeader>` is `sticky top-0 z-10`. A second sticky bar on the same page —
// the week headers on Farm Problems / Loads / Harvest Picks / the calendar
// agenda, the Workers search — used `top-0` too, so it pinned at the same
// place and slid UNDER the header: scrolling a register, you never saw which
// week you were in. Nothing on the page knew how tall the header was, and it
// is not one number: it wraps to two or three rows on a phone, carries the
// notch / banner inset, and on a phone held sideways it does not stick at all
// (W-15's `short:static`).
//
// So the header PUBLISHES its height, and the bars READ it:
//  - `usePublishPageHeaderHeight(ref)` — PageHeader writes `--page-header-h`
//    onto its parent (the page root), kept current by a ResizeObserver, and
//    writes 0px whenever the header is not sticky. Scoped to the page root,
//    not <html>: two pages never fight over one global, and a page root that
//    is itself the scroller keeps its value (inline beats the reset below).
//  - `useStickyBelowPageHeader()` — the bar's `top`. On a window-scroll page
//    that is the header's height. Inside an inner scroller (Scouting's list,
//    the Sprays Records list) `index.css` re-zeroes the variable, because the
//    scroller already starts below the header — and the hook subtracts the
//    scroller's padding-top, because a sticky child pins to the scroller's
//    CONTENT edge: with `top: 0` in a `p-4` list the week header stopped 16px
//    short and a strip of the card beneath showed above it.
import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react'

const VAR = '--page-header-h'

/** The offset a sticky header publishes: its height while it sticks, 0 when it
 *  does not (static — nothing is pinned for a bar to sit under). */
export function stickyOffsetPx(position: string, height: number): number {
  return position === 'sticky' ? Math.max(0, height) : 0
}

/** CSS `top` for a bar pinned under the page header, inside a scroller whose
 *  padding-top is `scrollerPadTop` px (0 for the window). */
export function belowHeaderTop(scrollerPadTop: number): string {
  return scrollerPadTop > 0
    ? `calc(var(${VAR}, 0px) - ${scrollerPadTop}px)`
    : `var(${VAR}, 0px)`
}

/** PageHeader only. Writes `--page-header-h` on the header's parent. */
export function usePublishPageHeaderHeight(ref: RefObject<HTMLElement>): void {
  useLayoutEffect(() => {
    const el = ref.current
    const host = el?.parentElement
    if (!el || !host) return
    const publish = () => {
      const px = stickyOffsetPx(getComputedStyle(el).position, el.getBoundingClientRect().height)
      host.style.setProperty(VAR, `${px}px`)
    }
    publish()
    // The height changes when the header wraps (a title, a Go-to button, a
    // stale-data cue appearing); `position` changes with the viewport (a phone
    // turned on its side), which the observer does not see — hence `resize`.
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(publish)
    ro?.observe(el)
    window.addEventListener('resize', publish)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', publish)
      host.style.removeProperty(VAR)
    }
  }, [ref])
}

const SCROLLS = /^(auto|scroll|hidden|overlay)$/

/** The nearest ancestor a sticky child pins to; null = the window. */
function scrollContainerOf(el: HTMLElement): HTMLElement | null {
  for (let a = el.parentElement; a && a !== document.body; a = a.parentElement) {
    if (SCROLLS.test(getComputedStyle(a).overflowY)) return a
  }
  return null
}

/** For a `sticky` bar that must pin directly under the page header. Spread
 *  `style` onto the bar (no `top-0` class) and attach `ref`. */
export function useStickyBelowPageHeader<T extends HTMLElement>(): { ref: RefObject<T>; style: CSSProperties } {
  const ref = useRef<T>(null)
  const [pad, setPad] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const sc = scrollContainerOf(el)
      setPad(sc ? parseFloat(getComputedStyle(sc).paddingTop) || 0 : 0)
    }
    measure()
    // A scroller can stop being one (W-15's `short:overflow-visible` on a phone
    // turned sideways) or change its padding at a breakpoint.
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])
  return { ref, style: { top: belowHeaderTop(pad) } }
}
